import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { api } from 'lib/request/api';
import { apiErrorMessage } from 'utils/api-errors';
import type {
  EntityResponse,
  EntityReconcileCandidate,
  EntityReconcileScoredResponse,
  EntityReconcileCreateResponse,
  EntityReconcileSignalResponse,
} from 'types/api-responses';
import { buildReconcileBullets } from './reconcile-bullets';
import { CANDIDATES_NOT_ASSEMBLED_MESSAGE } from './utils';
import {
  fetchScoredCandidates,
  newReconcileSessionId,
  undoReconciliation,
} from './api';

// The post-reconcile summary. `fromEntity*` captures the entity the reconcile
// was launched from — needed so Undo can navigate back when the viewed entity
// was retired. `sessionId` is captured AT RECONCILE TIME: the backend only
// honors an undo presented under the session the reconcile was created with.
export interface ReconcileSummary {
  result: EntityReconcileCreateResponse;
  fromEntityId: number;
  fromEntityName: string;
  survivorName: string;
  bullets: string[];
  sessionId: string;
  // Add-as-New conversions committed during this reconcile session (§3.14). Each
  // is its own transaction and is NOT reversed by Undo, so the confirmation must
  // disclose that they remain in effect when the count is non-zero.
  addAsNewCount: number;
}

export interface EntityReconcile {
  signalCount: number;
  signalVisible: boolean;
  dismissSignal: () => void;
  assembling: boolean;
  launch: () => void;
  // A launch that failed outright (network/server) — distinct from the genuine
  // all-eliminated response, which is a scoring verdict, not an error.
  launchError: string | null;
  dismissLaunchError: () => void;
  sessionId: string;
  tearsheetOpen: boolean;
  candidates: EntityReconcileCandidate[];
  // The viewed entity's own alias-family breakdown, for its right-panel row.
  viewedAliasGroup: EntityReconcileScoredResponse['viewedAliasGroup'];
  moreFound: boolean;
  totalBeforeCap: number;
  // Diagnostics only (R2): the mode the server actually used to produce the set
  // (reflects automatic lexical fallback), and whether the semantic capability
  // is enabled in this environment. No UI branches on these — there is no
  // user-facing lexical↔semantic toggle; the mode is server-decided.
  semantic: boolean | null;
  semanticAvailable: boolean;
  closeTearsheet: () => void;
  // Called by the tearsheet each time an in-place "Add as New" canonization
  // commits (§3.14) — the count feeds the Undo confirmation's disclosure.
  notchCanonize: () => void;
  complete: (
    result: EntityReconcileCreateResponse,
    namesById: Record<number, string>
  ) => void;
  allEliminatedOpen: boolean;
  closeAllEliminated: () => void;
  summary: ReconcileSummary | null;
  dismissSummary: () => void;
  undoModalOpen: boolean;
  requestUndo: () => void;
  cancelUndo: () => void;
  confirmUndo: () => void;
  undoing: boolean;
  undoError: string | null;
}

// Drives the Entity Page Reconciliation flow: the profile-load candidate signal,
// the Reconcile-click scoring fetch, and the post-reconcile summary + header
// Undo Reconcile (confirmation modal per the mock). All backend calls are plain
// ES-backed endpoints (no agent).
export function useEntityReconcile(
  entity: EntityResponse | undefined,
  getEntity: () => Promise<void>
): EntityReconcile {
  const history = useHistory();
  const entityId = entity?.id;
  const readOnly = !!entity?.deduplicatedAt || !!entity?.deleted;

  const [signalCount, setSignalCount] = useState(0);
  // The signal dismissal is per-entity: derived from the id it was dismissed
  // for, so navigating to another profile naturally un-dismisses.
  const [dismissedForId, setDismissedForId] = useState<number | null>(null);
  const [assembling, setAssembling] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [tearsheetOpen, setTearsheetOpen] = useState(false);
  const [candidates, setCandidates] = useState<EntityReconcileCandidate[]>([]);
  const [viewedAliasGroup, setViewedAliasGroup] =
    useState<EntityReconcileScoredResponse['viewedAliasGroup']>(null);
  const [moreFound, setMoreFound] = useState(false);
  const [totalBeforeCap, setTotalBeforeCap] = useState(0);
  // Server-decided retrieval mode + capability, captured for diagnostics only.
  const [semantic, setSemantic] = useState<boolean | null>(null);
  const [semanticAvailable, setSemanticAvailable] = useState(false);
  const [allEliminatedOpen, setAllEliminatedOpen] = useState(false);
  const [summary, setSummary] = useState<ReconcileSummary | null>(null);
  const [undoModalOpen, setUndoModalOpen] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  // A stable working-session token per page visit — NOT keyed on the entity id:
  // Case 2 navigates to the surviving entity's profile, and a regenerated token
  // would make the just-created reconciliation un-undoable (403).
  const [sessionId] = useState(newReconcileSessionId);

  // The page component survives /entities/:id navigations, so every async
  // response must check it still belongs to the entity on screen — a slow
  // signal or launch for entity A must not land on entity B's profile.
  const entityIdRef = useRef(entityId);
  entityIdRef.current = entityId;

  // Add-as-New conversions this session (§3.14). A ref, not state — it only needs
  // to be read at submit time, and bumping it must not re-render the tearsheet.
  const canonizeCountRef = useRef(0);
  const notchCanonize = useCallback(() => {
    canonizeCountRef.current += 1;
  }, []);

  const refreshSignal = useCallback((id: number) => {
    api
      .get(`entities/${id}/reconcile_signal`)
      .json<EntityReconcileSignalResponse>()
      .then((r) => {
        if (entityIdRef.current !== id) return; // navigated away — stale
        setSignalCount(r.count ?? 0);
      })
      .catch(() => {
        if (entityIdRef.current !== id) return;
        setSignalCount(0);
      });
  }, []);

  // Profile-load signal (async, non-blocking). Skipped on the read-only
  // deduplicated view.
  useEffect(() => {
    // A summary survives navigation only to the entities it belongs to (the
    // launched-from entity, or the Case-2 survivor) — any other profile must
    // not show a stale "Reconcile complete" banner + live Undo button.
    setSummary((prev) => {
      // While the entity is mid-reload its id is momentarily undefined (the
      // reconcile itself triggers that reload); keep the summary through that
      // window and only drop it once a genuinely different entity has loaded.
      const belongsHere =
        prev &&
        (entityId === undefined ||
          prev.fromEntityId === entityId ||
          prev.result.viewedEntityRetiredInto?.id === entityId);
      return belongsHere ? prev : null;
    });
    if (!entityId || readOnly) {
      setSignalCount(0);
      return;
    }
    refreshSignal(entityId);
  }, [entityId, readOnly, refreshSignal]);

  // Reconcile click → fetch the scored/collapsed/capped set, then open the
  // tearsheet (or the all-eliminated modal, Case 4). A fetch FAILURE is an
  // error state, never Case 4 — the modal's "no candidates met the threshold"
  // is a scoring verdict the backend didn't render.
  const launch = useCallback(() => {
    if (!entityId) return;
    setAssembling(true);
    setLaunchError(null);
    // Fresh session: a new set of Add-as-New conversions starts from zero.
    canonizeCountRef.current = 0;
    fetchScoredCandidates(entityId)
      .then((r) => {
        if (entityIdRef.current !== entityId) return; // navigated away — stale
        // Record the mode the server used (semantic vs its lexical fallback) and
        // the environment capability — diagnostics only; nothing branches on it.
        setSemantic(r.semantic ?? null);
        setSemanticAvailable(!!r.semanticAvailable);
        if (r.allEliminated || !r.candidates?.length) {
          setAllEliminatedOpen(true);
          return;
        }
        setCandidates(r.candidates);
        setViewedAliasGroup(r.viewedAliasGroup ?? null);
        setMoreFound(!!r.moreFound);
        setTotalBeforeCap(r.totalBeforeCap ?? r.candidates.length);
        setTearsheetOpen(true);
      })
      .catch(() => {
        if (entityIdRef.current !== entityId) return;
        setLaunchError(CANDIDATES_NOT_ASSEMBLED_MESSAGE);
      })
      .finally(() => setAssembling(false));
  }, [entityId]);

  const complete = useCallback(
    (
      result: EntityReconcileCreateResponse,
      namesById: Record<number, string>
    ) => {
      setTearsheetOpen(false);
      if (entity) {
        // "reconciled into X": the retired-into survivor when the viewed entity
        // was retired; else the first surviving id (the orchestrator lists
        // dedupe-group survivors before alias singletons).
        const survivorName =
          result.viewedEntityRetiredInto?.name ??
          namesById[result.survivingEntityIds[0]] ??
          entity.name;
        setSummary({
          result,
          fromEntityId: entity.id,
          fromEntityName: entity.name,
          survivorName,
          bullets: buildReconcileBullets(result, namesById),
          sessionId,
          addAsNewCount: canonizeCountRef.current,
        });
      }
      // Viewed entity retired → navigate to the surviving entity's profile
      // (the route change re-fetches the entity + its signal). Otherwise refresh
      // the current entity in place and re-run the signal (narrative: signal
      // refresh on return to profile).
      if (result.viewedEntityRetiredInto) {
        history.push(result.viewedEntityRetiredInto.href);
      } else {
        getEntity();
        if (entityId) refreshSignal(entityId);
      }
    },
    [entity, entityId, getEntity, history, refreshSignal, sessionId]
  );

  const confirmUndo = useCallback(() => {
    if (!summary) return;
    setUndoing(true);
    setUndoError(null);
    const { result, fromEntityId } = summary;
    undoReconciliation(fromEntityId, result.reconciliationId, summary.sessionId)
      .then(() => {
        setSummary(null);
        setUndoModalOpen(false);
        // If we navigated to the survivor because the viewed entity was retired,
        // Undo returns us to the original entity's profile.
        if (result.viewedEntityRetiredInto) {
          history.push(`/entities/${fromEntityId}`);
        } else {
          getEntity();
          if (entityId) refreshSignal(entityId);
        }
      })
      .catch(async (err) => {
        // Surface the server's reason (contention, out-of-session, …) in the
        // modal; the examiner can retry or cancel.
        setUndoError(await apiErrorMessage(err, 'Undo failed — try again.'));
      })
      .finally(() => setUndoing(false));
  }, [summary, history, getEntity, entityId, refreshSignal]);

  return {
    signalCount,
    signalVisible: signalCount > 0 && dismissedForId !== entityId,
    dismissSignal: () => setDismissedForId(entityId ?? null),
    assembling,
    launch,
    launchError,
    dismissLaunchError: () => setLaunchError(null),
    sessionId,
    tearsheetOpen,
    candidates,
    viewedAliasGroup,
    moreFound,
    totalBeforeCap,
    semantic,
    semanticAvailable,
    closeTearsheet: () => setTearsheetOpen(false),
    notchCanonize,
    complete,
    allEliminatedOpen,
    closeAllEliminated: () => setAllEliminatedOpen(false),
    summary,
    dismissSummary: () => setSummary(null),
    undoModalOpen,
    requestUndo: () => {
      setUndoError(null);
      setUndoModalOpen(true);
    },
    cancelUndo: () => setUndoModalOpen(false),
    confirmUndo,
    undoing,
    undoError,
  };
}
